
import React, { useState, useRef, useCallback } from 'react';
import { Person, Tool, Users } from '../../types';
import { UserIcon, LocationMarkerIcon, CameraIcon, XCircleIcon, CheckCircleIcon } from './Icons';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTools: Tool[];
  people: Users[];
  onAssign: (assigneeId: number, destination: string, photo?: File | null, notes?: string) => void;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({ isOpen, onClose, selectedTools, people, onAssign }) => {
  const [assigneeId, setAssigneeId] = useState<number>();
  const [destination, setDestination] = useState<string>('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [notes, setNotes] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!assigneeId || !destination || selectedTools.length === 0) {
      alert("Per favore seleziona un assegnatario, una destinazione d'uso e almeno un attrezzo.");
      return;
    }
    onAssign(assigneeId, destination, photo, notes);
    handleCloseModal();
  };

  const handleCloseModal = useCallback(() => {
    setAssigneeId(undefined);
    setDestination('');
    setPhoto(null);
    setPhotoPreview(null);
    setNotes('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-sky-400">Assegna Attrezzi</h2>
          <button 
            type="button" 
            onClick={handleCloseModal} 
            className="text-slate-400 hover:text-slate-200"
            aria-label="Chiudi"
            title="Chiudi"
          >
            <XCircleIcon className="w-8 h-8" />
          </button>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-medium text-slate-300 mb-2">Attrezzi Selezionati:</h3>
          {selectedTools.length > 0 ? (
            <ul className="list-disc list-inside bg-slate-700 p-3 rounded-md max-h-32 overflow-y-auto">
              {selectedTools.map(tool => <li key={tool.id} className="text-slate-300 text-sm">{tool.txt}</li>)}
            </ul>
          ) : (
            <p className="text-slate-400 text-sm">Nessun attrezzo selezionato.</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="assignee" className="block text-sm font-medium text-slate-300 mb-1">
            <UserIcon className="w-5 h-5 inline mr-2 align-text-bottom" />Assegna a:
          </label>
          <select
            id="assignee"
            value={assigneeId}
            onChange={(e) => setAssigneeId(Number.parseInt(e.target.value))}
            className="w-full p-2.5 rounded-md bg-slate-700 text-slate-200 border border-slate-600 focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="">Seleziona Persona</option>
            {people.map(person => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="destination" className="block text-sm font-medium text-slate-300 mb-1">
            <LocationMarkerIcon className="w-5 h-5 inline mr-2 align-text-bottom" />Destinazione:
          </label>
          <input
            type="text"
            id="destination"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="Es. Zamparrone, Cantiere X, Andrea (uso personale)"
            className="w-full p-2.5 rounded-md bg-slate-700 text-slate-200 border border-slate-600 focus:ring-sky-500 focus:border-sky-500 placeholder-slate-500"
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="notes" className="block text-sm font-medium text-slate-300 mb-1">Note Aggiuntive:</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Eventuali note sull'assegnazione..."
            className="w-full p-2.5 rounded-md bg-slate-700 text-slate-200 border border-slate-600 focus:ring-sky-500 focus:border-sky-500 placeholder-slate-500"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="photo-upload" className="block text-sm font-medium text-slate-300 mb-1">
            <CameraIcon className="w-5 h-5 inline mr-2 align-text-bottom" />Foto di Conferma:
          </label>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handlePhotoChange}
            className="hidden"
            aria-label="Carica foto di conferma"
            title="Carica foto di conferma"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center px-4 py-2.5 border border-dashed border-slate-600 rounded-md text-slate-400 hover:bg-slate-700 hover:text-sky-400 transition"
          >
            <CameraIcon className="w-5 h-5 mr-2" />
            {photoPreview ? 'Cambia Foto' : 'Carica Foto'}
          </button>
          {photoPreview && (
            <div className="mt-3 relative">
              <img src={photoPreview} alt="Anteprima foto" className="max-h-48 w-auto rounded-md mx-auto shadow-md" />
              <button 
                type="button"
                onClick={() => { setPhoto(null); setPhotoPreview(null); if(fileInputRef.current) fileInputRef.current.value = ''; }}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                title="Rimuovi foto"
                aria-label="Rimuovi foto"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCloseModal}
            className="px-6 py-2.5 rounded-md bg-slate-600 text-slate-200 hover:bg-slate-500 transition"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!assigneeId || !destination || selectedTools.length === 0}
            className="px-6 py-2.5 rounded-md bg-sky-600 text-white hover:bg-sky-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <CheckCircleIcon className="w-5 h-5 mr-2"/>
            Conferma Assegnazione
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
